
import api from '@/services/api';
import router from '@/router';
import {Module} from 'vuex';
import {RootState} from '@/types';
import { UserNotification, IUserNotification } from '@/classes/UserNotification';
import { ReleaseNote, IReleaseNote } from '@/classes/ReleaseNote';
import { INotificationsControllerRequest, INotificationsControllerResponse } from '@/classes/ControllerInterface/NotificationsController';
import moment from 'moment';

export interface NotificationsState {
    userNotifications: UserNotification[];
    releaseNotes: ReleaseNote[];
}

export const NotificationsStore: Module<NotificationsState, RootState> = {
    namespaced: true,

    state: {
        userNotifications: [],
        releaseNotes: []
    },

    getters: {
        getUserNotificationById: (state) => (id: number): UserNotification | null => {
            const index: number = state.userNotifications.findIndex((notification: UserNotification) => notification.id === Number(id));
            if (index !== -1) {
                return state.userNotifications[index];
            } else {
                return null;
            }
        },
        getUserNotifications: (state): UserNotification[] => {
            return state.userNotifications;
        },
        getReleaseNoteById: (state) => (id: number): ReleaseNote | null => {
            const index: number = state.releaseNotes.findIndex((release: ReleaseNote) => release.id === Number(id));
            if (index !== -1) {
                return state.releaseNotes[index];
            } else {
                return null;
            }
        },
        getReleaseNotes: (state): ReleaseNote[] => {
            return state.releaseNotes;
        },
        getCurrentReleaseNotes: (state): ReleaseNote[] => {
            return state.releaseNotes.filter((release: ReleaseNote) => {
                if (release.publishDateTime !== undefined && release.publishDateTime !== null) {
                    return moment(release.publishDateTime).isBefore(new Date());
                } else {
                    return false;
                }
            });
        },
        getViewableUserNotifications: (state): UserNotification[] => {
            return state.userNotifications.filter((notification: UserNotification) => {
                let beforeExpiry: boolean = false;
                if (notification.expirationDateTime !== undefined && notification.expirationDateTime !== null) {
                    beforeExpiry =  moment(notification.expirationDateTime).isAfter(new Date());
                }
                let afterPublish: boolean = false;
                if (notification.publishDateTime !== undefined && notification.publishDateTime !== null) {
                    afterPublish =  moment(notification.publishDateTime).isBefore(new Date());
                }
                return beforeExpiry && afterPublish;
            });
        }
    },

    mutations: {
        setUserNotifications(state, userNotifications: UserNotification[]) {
            state.userNotifications = userNotifications;
        },
        setReleaseNotes(state, releaseNotes: ReleaseNote[]) {
            state.releaseNotes = releaseNotes;
        }
    },

    actions: {
        loadUserNotifications({ commit, state }, forceRefresh: boolean) {
            return new Promise((resolve, reject) => {
                if (state.userNotifications.length === 0 || forceRefresh) {
                    api().get('notifications/usernotifications')
                    .then((res) => {
                        const response: INotificationsControllerResponse = res.data;
                        if (response.success) {
                            const notificationsJSON = response.userNotifications !== undefined ? response.userNotifications : [];
                            const notifications: UserNotification[] = [];
                            notificationsJSON.forEach((element: any) => {
                                const notification: UserNotification = new UserNotification(element as IUserNotification);
                                notifications.push(notification);
                            });
                            commit('setUserNotifications', notifications);
                        }
                        resolve();
                    })
                    .catch((e) => {
                        reject(e);
                    });
                } else {
                    resolve();
                }
            });
        },
        addUserNotification({ dispatch }, userNotification: UserNotification) {
            return new Promise((resolve, reject) => {
                api().post('notifications/usernotification', {userNotification:
                    {...userNotification,
                        publishDateTime: userNotification.publishDateTime,
                        expirationDateTime: userNotification.expirationDateTime
                    } as IUserNotification} as INotificationsControllerRequest)
                .then((res) => {
                    const response: INotificationsControllerResponse = res.data;
                    if (response.success && response.userNotification && response.userNotification.id) {
                        dispatch('loadUserNotifications')
                        .then(() => {
                            alert('User Notification saved!');
                            if (response.userNotification !== undefined && response.userNotification !== null &&
                                response.userNotification.id !== undefined && response.userNotification.id !== null) {
                                router.push('/administration/usernotifications/notification/' + response.userNotification.id);
                                resolve();
                            } else {
                                reject();
                            }
                        });
                    } else {
                        reject();
                    }
                })
                .catch((e) => {
                    reject(e);
                });
            });
        },
        editUserNotification({ dispatch }, userNotification: UserNotification) {
            return new Promise((resolve, reject) => {
                api().patch('notifications/usernotification', {userNotification:
                    {...userNotification,
                        publishDateTime: userNotification.publishDateTime,
                        expirationDateTime: userNotification.expirationDateTime
                    } as IUserNotification} as INotificationsControllerRequest)
                .then((res) => {
                    const response: INotificationsControllerResponse = res.data;
                    if (response.success && response.userNotification && response.userNotification.id) {
                        dispatch('loadUserNotifications')
                        .then(() => {
                            alert('User Notification saved!');
                            if (response.userNotification !== undefined && response.userNotification !== null &&
                                response.userNotification.id !== undefined && response.userNotification.id !== null) {
                                router.push('/administration/usernotifications/notification/' + response.userNotification.id);
                                resolve();
                            } else {
                                reject();
                            }
                        });
                    } else {
                        reject();
                    }
                })
                .catch((e) => {
                    reject(e);
                });
            });
        },
        deleteUserNotification({ dispatch }, userNotification: UserNotification) {
            return new Promise((resolve, reject) => {
                api().delete('notifications/usernotification', { data: {userNotification:
                    {...userNotification,
                        publishDateTime: userNotification.publishDateTime,
                        expirationDateTime: userNotification.expirationDateTime
                    } as IUserNotification} as INotificationsControllerRequest })
                .then((res) => {
                    const response: INotificationsControllerResponse = res.data;
                    if (response.success) {
                        dispatch('loadUserNotifications')
                        .then(() => {
                            alert('User Notification deleted!');
                            router.push('/administration/usernotifications');
                            resolve();
                        });
                    } else {
                        reject();
                    }
                })
                .catch(() => {
                    reject();
                });
            });
        },
        loadReleaseNotes({ commit, state }, forceRefresh: boolean) {
            return new Promise((resolve, reject) => {
                if (state.releaseNotes.length === 0 || forceRefresh) {
                    api().get('notifications/releasenotes')
                    .then((res) => {
                        const response: INotificationsControllerResponse = res.data;
                        if (response.success) {
                            const releaseNotesJSON: IReleaseNote[] = response.releaseNotes !== undefined ? response.releaseNotes : [];
                            const releaseNotes: ReleaseNote[] = [];
                            releaseNotesJSON.forEach((element: IReleaseNote) => {
                                const releaseNote: ReleaseNote = new ReleaseNote(element);
                                releaseNotes.push(releaseNote);
                            });
                            commit('setReleaseNotes', releaseNotes);
                        }
                        resolve();
                    })
                    .catch((e) => {
                        reject(e);
                    });
                } else {
                    resolve();
                }
            });
        },
        addReleaseNote({ dispatch }, releaseNote: ReleaseNote) {
            return new Promise((resolve, reject) => {
                api().post('notifications/releasenote', {releaseNote:
                    {...releaseNote,
                        publishDateTime: releaseNote.publishDateTime
                    } as IReleaseNote} as INotificationsControllerRequest)
                .then((res) => {
                    const response: INotificationsControllerResponse = res.data;
                    if (response.success && response.releaseNote && response.releaseNote.id) {
                        dispatch('loadReleaseNotes')
                        .then(() => {
                            alert('Release note saved!');
                            if (response.releaseNote !== undefined && response.releaseNote !== null &&
                                response.releaseNote.id !== undefined && response.releaseNote.id !== null) {
                                router.push('/administration/releasenotes/notification/' + response.releaseNote.id);
                                resolve();
                            } else {
                                reject();
                            }
                        });
                    } else {
                        reject();
                    }
                })
                .catch((e) => {
                    reject(e);
                });
            });
        },
        editReleaseNote({ dispatch }, releaseNote: ReleaseNote) {
            return new Promise((resolve, reject) => {
                api().patch('notifications/releasenote', {releaseNote:
                    {...releaseNote,
                        publishDateTime: releaseNote.publishDateTime
                    } as IReleaseNote} as INotificationsControllerRequest)
                .then((res) => {
                    const response: INotificationsControllerResponse = res.data;
                    if (response.success && response.releaseNote && response.releaseNote.id) {
                        dispatch('loadReleaseNotes')
                        .then(() => {
                            alert('Release note saved!');
                            if (response.releaseNote !== undefined && response.releaseNote !== null &&
                                response.releaseNote.id !== undefined && response.releaseNote.id !== null) {
                                router.push('/administration/releasenotes/notification/' + response.releaseNote.id);
                                resolve();
                            } else {
                                reject();
                            }
                        });
                    } else {
                        reject();
                    }
                })
                .catch((e) => {
                    reject(e);
                });
            });
        },
        deleteReleaseNote({ dispatch }, releaseNote: ReleaseNote) {
            return new Promise((resolve, reject) => {
                api().delete('notifications/releasenote', { data: {releaseNote:
                    {...releaseNote,
                        publishDateTime: releaseNote.publishDateTime
                    } as IReleaseNote} as INotificationsControllerRequest })
                .then((res) => {
                    const response: INotificationsControllerResponse = res.data;
                    if (response.success) {
                        dispatch('loadReleaseNotes')
                        .then(() => {
                            alert('Release note deleted!');
                            router.push('/administration/releasenotes');
                            resolve();
                        });
                    } else {
                        reject();
                    }
                })
                .catch(() => {
                    reject();
                });
            });
        }
    }
};
